import { isAPIError } from "@/core/services";
import type { ToastApi } from "@/ds";
import { createRole, updateRole } from "@/modules/roles";
import type { Role } from "@/modules/roles";
import {
  CREATE_ERROR_MESSAGE,
  CREATE_FORBIDDEN_MESSAGE,
  CREATE_SUCCESS_MESSAGE,
  CREATE_SUCCESS_SUMMARY,
  UPDATE_ERROR_MESSAGE,
  UPDATE_FORBIDDEN_MESSAGE,
  UPDATE_SUCCESS_MESSAGE,
  UPDATE_SUCCESS_SUMMARY,
} from "../roles.constants";

export interface RoleFormValues {
  readonly nome: string;
  /** RN02: conjunto completo de claims marcado; o serviço sincroniza as associações. */
  readonly claims: string[];
}

interface UseRolesGravacaoParams {
  readonly toast: ToastApi;
  /** Sucesso: a tela fecha o modal e refaz a consulta. */
  readonly onSaved: () => void;
}

/**
 * Model do cadastro e da edição (HU 864). A mensagem de nome duplicado (409 do core) ainda não é decidida: a falha
 * segue o MS003/MS004 genérico, num ponto só, `falhar`.
 */
export function useRolesGravacao({ toast, onSaved }: UseRolesGravacaoParams) {
  function falhar(error: unknown, isEditing: boolean) {
    const isForbidden = isAPIError(error) && error.status === 403;

    toast.error(
      isForbidden
        ? "Acesso não autorizado"
        : isEditing
          ? UPDATE_ERROR_MESSAGE
          : CREATE_ERROR_MESSAGE,
      isForbidden ? (isEditing ? UPDATE_FORBIDDEN_MESSAGE : CREATE_FORBIDDEN_MESSAGE) : undefined,
    );
  }

  /** Nunca rejeita: a falha mantém o modal aberto com os dados preenchidos. */
  return async (role: Role | null, values: RoleFormValues): Promise<void> => {
    try {
      if (role) {
        await updateRole({ id: role.id, ...values });
      } else {
        await createRole(values);
      }
    } catch (error) {
      falhar(error, Boolean(role));
      return;
    }

    onSaved();
    toast.success(
      role ? UPDATE_SUCCESS_SUMMARY : CREATE_SUCCESS_SUMMARY,
      role ? UPDATE_SUCCESS_MESSAGE : CREATE_SUCCESS_MESSAGE,
    );
  };
}
